/**
 * ChatEmptyStateV2 — the V2 pre-chat empty state with answerable suggestion
 * prompts (bead terra-design-v2-chat-yub.7, spec p1.s5).
 *
 * Visual shape ported from the prototype's `ChatEmptyState`
 * (`clayground-sc-terra-flow-panels/projects/sc-terra-flow/src/chat/ChatEmptyState.tsx`)
 * — a title, one `Text size="xs" variant="tertiary"` description line, and a
 * stack of suggestion rows — but NOT its data or icon map: this component
 * reuses Terra's real `EXAMPLE_QUESTIONS`
 * (`account-view/keystone/chat-panel-core.tsx`), the account-scoped
 * questions Keystone actually answers today, never the prototype's own
 * suggestion list (cross-account questions outside this account's data that
 * fail on the real agent — brief §7; this bead's checkpoint greps for that
 * list's exact wording, so it is deliberately not quoted here either).
 *
 * Suggestion rows are plain Glaze primitives local to this folder (bead
 * .7's own "Resolved" Q2 answer — there is no shared `shell-next/ui/` row
 * component yet; if one appears later, this migrates to it). Built on a
 * native `<button>` rather than the prototype's `Element`/Glaze `Card` —
 * same reasoning `Element.tsx`'s own doc comment gives for avoiding `Card`
 * (its `onClick` prop wraps it in a `<HeadlessButton>`), except here there's
 * nothing nested inside a row that a native button's own keyboard/role
 * semantics would conflict with, so the native element is the simpler
 * choice.
 *
 * Title is `Ask about ${accountLabel}` rather than the prototype's
 * account-agnostic "Ask about any account" — every Flow chat session is
 * already scoped to one account (bead .7's own spec).
 */
import { Icon } from '@clay-run/glaze/icon';
import { Strong, Text } from '@clay-run/glaze/text';
import { FLOW_ICONS } from '../../icons';
import { EXAMPLE_QUESTIONS } from '../../../account-view/keystone/chat-panel-core';

export interface ChatEmptyStateV2Props {
  accountLabel: string | undefined;
  /**
   * Seeds the composer's draft with the clicked suggestion's exact text —
   * does NOT send it (matches V1's seed-then-send flow, chat-panel-core.tsx's
   * `EmptyState`/`onExampleClick`). Callers pass `controller.handleExampleClick`
   * from `useFlowChatSessionController` (bead .1).
   */
  onSelectSuggestion: (question: string) => void;
}

export function ChatEmptyStateV2({ accountLabel, onSelectSuggestion }: ChatEmptyStateV2Props) {
  return (
    <div
      data-testid="chat-empty-state-v2"
      className="flex-1 min-h-0 flex flex-col items-center justify-center overflow-y-auto px-4 py-8"
    >
      <div className="w-full max-w-sm text-center">
        <Strong className="text-sm">{`Ask about ${accountLabel ?? 'this account'}`}</Strong>
        <Text size="xs" variant="tertiary" className="mt-1 mb-5 block">
          Powered by Keystone AI
        </Text>
        <div className="flex w-full flex-col gap-1.5">
          {EXAMPLE_QUESTIONS.map((question) => (
            <button
              key={question}
              type="button"
              onClick={() => onSelectSuggestion(question)}
              className="flex w-full items-center justify-between gap-3 rounded-xl border border-border-secondary bg-bg-primary px-4 py-2.5 text-left transition-colors hover:border-border-primary-hover hover:bg-bg-secondary"
            >
              <Text size="sm" variant="secondary" className="min-w-0 flex-1 truncate">
                {question}
              </Text>
              <Icon type={FLOW_ICONS.goTo} size="sm" className="shrink-0 text-content-faint" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
